/**
 * Admin → Users.
 *
 * Lists every authorized user. Click a row to open `UserDetailDrawer`.
 * "Add user" opens `UserAddModal`.
 */
import { useMemo, useState } from "react";

import { adminApi } from "../../api/admin";
import { PageHeader } from "../../components/PageHeader";
import { Pill } from "../../components/Pill";
import { QueryBoundary } from "../../components/QueryBoundary";
import { formatRelative } from "../../lib/formatRelative";
import { UserAddModal } from "./UserAddModal";
import { UserDetailDrawer } from "./UserDetailDrawer";

export function UsersPage() {
  const [filter, setFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [openUserId, setOpenUserId] = useState<string | null>(null);

  const usersQuery = adminApi.users.list();
  const auditQuery = adminApi.audit.query({ limit: 200 });

  const lastSeenByEmail = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of auditQuery.data ?? []) {
      if (!map.has(entry.user_email)) {
        map.set(entry.user_email, entry.created_at);
      }
    }
    return map;
  }, [auditQuery.data]);

  const filtered = useMemo(() => {
    const list = usersQuery.data ?? [];
    if (!filter.trim()) return list;
    const q = filter.toLowerCase();
    return list.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.name ?? "").toLowerCase().includes(q),
    );
  }, [usersQuery.data, filter]);

  return (
    <>
      <PageHeader
        title="Users"
        description="Authorized users for Jarvis. Manage roles, Slack mapping, and cron access."
        actions={
          <button
            type="button"
            className="btn btn-main"
            onClick={() => setAddOpen(true)}
          >
            Add user
          </button>
        }
      />

      <div className="flex items-end gap-4 mb-5">
        <div className="flex-1 max-w-md">
          <label className="label" htmlFor="users-search">
            Search
          </label>
          <input
            id="users-search"
            className="input"
            placeholder="filter by email or name…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className="text-sm text-ink-low pb-2">
          <span className="tnum font-medium text-ink">{filtered.length}</span>{" "}
          listed
        </div>
      </div>

      <QueryBoundary
        query={usersQuery}
        empty={
          <div className="empty-state">
            <div className="title">No users</div>
            <p className="hint">Add the first user with the button above.</p>
          </div>
        }
        isEmpty={() => filtered.length === 0}
      >
        {() => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Roles</th>
                  <th>Cron</th>
                  <th>Last activity</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => {
                  const lastSeen = lastSeenByEmail.get(user.email);
                  const isDisabled = !!user.disabled_at;
                  return (
                    <tr
                      key={user.id}
                      onClick={() => setOpenUserId(user.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <span className="font-heading font-semibold text-ink">
                          {user.email}
                        </span>
                      </td>
                      <td className="text-sm text-ink-medium">
                        {user.name || "—"}
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {user.role_names.length === 0 ? (
                            <span className="text-ink-low text-xs">—</span>
                          ) : (
                            user.role_names.map((role) => (
                              <span key={role} className="tag-chip">
                                {role}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td>
                        {user.cron_enabled ? (
                          <Pill value="active" />
                        ) : (
                          <Pill value="paused" />
                        )}
                      </td>
                      <td className="text-sm text-ink-low tnum">
                        {lastSeen ? formatRelative(lastSeen) : "—"}
                      </td>
                      <td>
                        {isDisabled ? (
                          <Pill value="dismissed" />
                        ) : (
                          <Pill value="active" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>

      <UserAddModal open={addOpen} onClose={() => setAddOpen(false)} />
      <UserDetailDrawer
        userId={openUserId}
        onClose={() => setOpenUserId(null)}
      />
    </>
  );
}
